import './MaterialForm.css';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { createMaterial, updateMaterial } from '../../services/materialService';
import ConfirmationModal from '../../components/ConfirmationModal';
import { createDuplicateMaterialDraft } from './materialDuplicate';
import {
  MATERIAL_MECHANICAL_PROPERTY_SCALES,
  prepareMaterialFormPayload,
  resolveMaterialMechanicalProperty,
} from './materialMechanicalProperty';

const createInitialMaterialData = () => ({
  materialName: '',
  otherNames: [],
  mechanicalProperty: { value: '', scale: 'MPa_Rm' },
  thermalConductivity: '',
  elongation: '',
  chemicalComposition: [],
  materialGroup: '',
  coefficients: { Vc: '', Fz: '', Ap: '', Ae: '' },
  materialDescription: '',
  isChecked: false,
});

const MaterialForm = () => {
  const location = useLocation();
  const selectedMaterial = location.state?.material;
  const isDuplicate = location.state?.mode === 'duplicate';

  const [materialData, setMaterialData] = useState(createInitialMaterialData);

  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedMaterial && isDuplicate) {
      setMaterialData(createDuplicateMaterialDraft(selectedMaterial));
      setIsEditing(false);
    } else if (selectedMaterial) {
      setMaterialData({
        ...createInitialMaterialData(),
        ...selectedMaterial,
        mechanicalProperty: resolveMaterialMechanicalProperty(selectedMaterial)
          || { value: '', scale: 'MPa_Rm' },
      });
      setIsEditing(true);
    }
  }, [isDuplicate, selectedMaterial]);

  const addOtherName = () => {
    setMaterialData({
      ...materialData,
      otherNames: [...materialData.otherNames, '']
    });
  };

  const removeOtherName = (index) => {
    const newOtherNames = materialData.otherNames.filter((_, i) => i !== index);
    setMaterialData({
      ...materialData,
      otherNames: newOtherNames
    });
  };

  const handleOtherNameChange = (index, value) => {
    const newOtherNames = materialData.otherNames.map((name, i) => {
      if (i === index) {
        return value;
      }
      return name;
    });
    setMaterialData({
      ...materialData,
      otherNames: newOtherNames
    });
  };

  const addChemicalComposition = () => {
    setMaterialData({
      ...materialData,
      chemicalComposition: [...materialData.chemicalComposition, { element: '', percentage: '' }]
    });
  };

  const removeChemicalComposition = (index) => {
    const newChemicalComposition = materialData.chemicalComposition.filter((_, i) => i !== index);
    setMaterialData({
      ...materialData,
      chemicalComposition: newChemicalComposition
    });
  };

  const handleChemicalCompositionChange = (index, field, value) => {
    const newChemicalComposition = materialData.chemicalComposition.map((composition, i) => {
      if (i === index) {
        return { ...composition, [field]: value };
      }
      return composition;
    });
    setMaterialData({
      ...materialData,
      chemicalComposition: newChemicalComposition
    });
  };

  const submitMaterialData = (e) => {
    e.preventDefault();
    try {
      setFormError('');
      setFormSuccess('');
      const preparedMaterial = prepareMaterialFormPayload(materialData);
      const { _id, ...updates } = preparedMaterial;
      if (isEditing && !_id) throw new Error('Material ID is missing for editing.');
      setPendingSubmission({ id: _id, payload: isEditing ? updates : preparedMaterial });
    } catch (error) {
      setFormError(error?.message || 'There was an error submitting the material data. Please try again.');
    }
  };

  const confirmSubmission = async () => {
    if (!pendingSubmission || isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');
    try {
      if (isEditing) {
        await updateMaterial(pendingSubmission.id, pendingSubmission.payload);
        setFormSuccess('Material successfully updated.');
      } else {
        await createMaterial(pendingSubmission.payload);
        setFormSuccess('Material successfully created.');
      }
      setMaterialData(createInitialMaterialData());
      setIsEditing(false);
      setPendingSubmission(null);
    } catch (error) {
      console.error('Error submitting material data:', error);
      setFormError(error?.response?.data?.message || error?.message
        || 'There was an error submitting the material data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <form className="material-form" onSubmit={submitMaterialData}>
      <h2>Material Form</h2>
        <div className="group-all-other">
          <div className="group-material-info">
            <label htmlFor="materialName">Material name:</label>
            <input
              type="text"
              id="materialName"
              name="materialName"
              value={materialData.materialName}
              onChange={(e) => setMaterialData({ ...materialData, materialName: e.target.value })}
              placeholder="Material Name"
              className="margin-bottom"
            />

            <fieldset className="mechanical-property-fields margin-bottom">
              <legend>Mechanical strength / hardness</legend>
              <label htmlFor="mechanicalPropertyValue">Value:</label>
              <input
                type="number"
                id="mechanicalPropertyValue"
                name="mechanicalPropertyValue"
                value={materialData.mechanicalProperty.value}
                onChange={(e) => setMaterialData({
                  ...materialData,
                  mechanicalProperty: { ...materialData.mechanicalProperty, value: e.target.value },
                })}
                step="any"
                min="0"
                required
              />
              <label htmlFor="mechanicalPropertyScale">Scale:</label>
              <select
                id="mechanicalPropertyScale"
                name="mechanicalPropertyScale"
                value={materialData.mechanicalProperty.scale}
                onChange={(e) => setMaterialData({
                  ...materialData,
                  mechanicalProperty: { ...materialData.mechanicalProperty, scale: e.target.value },
                })}
                required
              >
                {MATERIAL_MECHANICAL_PROPERTY_SCALES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </fieldset>

            <label htmlFor="thermalConductivity">Thermal conductivity:</label>
            <input
              type="number"
              id="thermalConductivity"
              name="thermalConductivity"
              value={materialData.thermalConductivity}
              onChange={(e) => setMaterialData({ ...materialData, thermalConductivity: e.target.value })}
              placeholder="Thermal Conductivity"
              className="margin-bottom"
            />

            <label htmlFor="elongation">Elongation:</label>
            <input
              type="number"
              id="elongation"
              name="elongation"
              value={materialData.elongation}
              onChange={(e) => setMaterialData({ ...materialData, elongation: e.target.value })}
              placeholder="Elongation"
              className="margin-bottom"
            />

            <label htmlFor="chemicalComposition">Chemical composition:</label>
            {materialData.chemicalComposition.map((composition, index) => (
              <div key={index}>
                <input
                  type="text"
                  value={composition.element}
                  onChange={(e) => handleChemicalCompositionChange(index, 'element', e.target.value)}
                  placeholder="Element"
                />
                <input
                  type="number"
                  value={composition.percentage}
                  onChange={(e) => handleChemicalCompositionChange(index, 'percentage', e.target.value)}
                  placeholder="Percentage"
                />
                <button type="button" className="margin-bottom" onClick={() => removeChemicalComposition(index)}>Remove</button>
              </div>
            ))}
            <button type="button" onClick={addChemicalComposition}>Add Chemical Composition</button>
          </div>

          <div className="group-other-names-coefficients">
            <label htmlFor="otherNames">Other names:</label>
            {materialData.otherNames.map((name, index) => (
              <div key={index}>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleOtherNameChange(index, e.target.value)}
                  placeholder="Other Name"
                />
                <button type="button" className="margin-bottom" onClick={() => removeOtherName(index)}>Remove</button>
              </div>
            ))}
            <button type="button" className="margin-bottom" onClick={addOtherName}>Add Other Name</button>

            <label htmlFor="materialGroup">Material group:</label>
            <input
              type="number"
              id="materialGroup"
              name="materialGroup"
              value={materialData.materialGroup}
              onChange={(e) => setMaterialData({ ...materialData, materialGroup: e.target.value })}
              placeholder="Material Group"
              className="margin-bottom"
            />

            <label htmlFor="Coefficients">Coefficients:</label>
            <label htmlFor="Vc">Vc:</label>
            <input
              type="number"
              id="Vc"
              name="Vc"
              value={materialData.coefficients?.Vc || ''}
              onChange={(e) => setMaterialData({ ...materialData, coefficients: { ...materialData.coefficients, Vc: parseFloat(e.target.value) } })}
              placeholder="Coefficient Vc"
              className="margin-bottom"
            />
            <label htmlFor="Fz">Fz:</label>
            <input
              type="number"
              id="Fz"
              name="Fz"
              value={materialData.coefficients?.Fz || ''}
              onChange={(e) => setMaterialData({ ...materialData, coefficients: { ...materialData.coefficients, Fz: parseFloat(e.target.value) } })}
              placeholder="Coefficient Fz"
              className="margin-bottom"
            />
            <label htmlFor="Ap">Ap:</label>
            <input
              type="number"
              id="Ap"
              name="Ap"
              value={materialData.coefficients?.Ap || ''}
              onChange={(e) => setMaterialData({ ...materialData, coefficients: { ...materialData.coefficients, Ap: parseFloat(e.target.value) } })}
              placeholder="Coefficient Ap"
              className="margin-bottom"
            />
            <label htmlFor="Ae">Ae:</label>
            <input
              type="number"
              id="Ae"
              name="Ae"
              value={materialData.coefficients?.Ae || ''}
              onChange={(e) => setMaterialData({ ...materialData, coefficients: { ...materialData.coefficients, Ae: parseFloat(e.target.value) } })}
              placeholder="Coefficient Ae"
              className="margin-bottom"
            />
          </div>
        </div>

        <div className="group-material-description">
          <label htmlFor="materialDescription">Material description:</label>
          <textarea
            id="materialDescription"
            name="materialDescription"
            value={materialData.materialDescription || ''}
            onChange={(e) => setMaterialData({ ...materialData, materialDescription: e.target.value })}
            placeholder="Material Description"
            rows="4"
          />
        </div>

        <div className="group-admin-check">
          <label htmlFor="isChecked">Checked by admin:</label>
          <input
            type="checkbox"
            id="isChecked"
            name="isChecked"
            checked={!!materialData.isChecked}
            onChange={(e) => setMaterialData({ ...materialData, isChecked: e.target.checked })}
            className="margin-bottom"
          />
        </div>

        <div className="group-submit">
          {formError && <p className="material-form-error" role="alert">{formError}</p>}
          {formSuccess && <p className="material-form-success" role="status">{formSuccess}</p>}
          <button type="submit" disabled={isSubmitting}>{isEditing ? 'Save changes' : 'Create'}</button>
        </div>
    </form>
    <ConfirmationModal
      open={!!pendingSubmission}
      title={isEditing
        ? `Save changes to "${materialData.materialName}"?`
        : `Create material "${materialData.materialName}"?`}
      confirmLabel={isEditing ? 'Save changes' : 'Create'}
      onCancel={() => setPendingSubmission(null)}
      onConfirm={confirmSubmission}
      submitting={isSubmitting}
    />
    </>
  );
};

export default MaterialForm;
